import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { useGetOrderDetailsQuery } from '../slices/ordersApiSlice';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';

const MOYASAR_CSS = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.css';
const MOYASAR_JS = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.js';

const loadMoyasar = () =>
  new Promise((resolve, reject) => {
    if (window.Moyasar) return resolve(window.Moyasar);
    if (!document.querySelector(`link[href="${MOYASAR_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = MOYASAR_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${MOYASAR_JS}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Moyasar));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = MOYASAR_JS;
    script.onload = () => resolve(window.Moyasar);
    script.onerror = reject;
    document.head.appendChild(script);
  });

const MoyasarScreen = () => {
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetOrderDetailsQuery(orderId);
  const [formError, setFormError] = useState(false);
  const initialized = useRef(false);
  const { t, i18n } = useTranslation();
  const isAr = i18n.language !== 'en';

  const order = data?.order || data;
  const items = data?.orderItems || [];

  useEffect(() => {
    if (!order || initialized.current) return;
    if (order.isPaid) {
      navigate(`/orders/${orderId}`);
      return;
    }
    initialized.current = true;
    loadMoyasar()
      .then((Moyasar) => {
        Moyasar.init({
          element: '.mysr-form',
          amount: Math.round(parseFloat(order.totalPrice) * 100),
          currency: 'SAR',
          description: `Tamrat order #${orderId}`,
          publishable_api_key: process.env.REACT_APP_MOYASAR_PK,
          callback_url: `${window.location.origin}/success?orderApi=${orderId}`,
          methods: ['creditcard', 'stcpay', 'applepay'],
          apple_pay: {
            country: 'SA',
            label: 'Tamrat Dates',
            validate_merchant_url: 'https://api.moyasar.com/v1/applepay/initiate',
          },
          supported_networks: ['mada', 'visa', 'mastercard'],
          metadata: { order_id: String(orderId) },
          language: isAr ? 'ar' : 'en',
        });
      })
      .catch(() => setFormError(true));
  }, [order, orderId, navigate, isAr]);

  const itemName = (it) =>
    isAr
      ? (it.product?.name_ar || it.product?.name || it.name_ar || it.name)
      : (it.product?.name_en || it.product?.name || it.name_en || it.name);

  return (
    <>
      <Meta title={isAr ? 'تمرات - إتمام الدفع' : 'Tamrat Dates - Complete Payment'} />
      <Container>
        <Row className='justify-content-center mt-4 mb-5'>
          <Col xs={12} md={8} lg={6}>
            <Link to={`/orders/${orderId}`} style={{ fontSize: 14, color: '#7C9D64', textDecoration: 'none' }}>
              {isAr ? '→ العودة للطلب' : '← Back to order'}
            </Link>

            <h1 className='text-center mb-3 mt-2'>{isAr ? 'إتمام الدفع' : 'Complete your payment'}</h1>

            {order && (
              <div
                style={{
                  background: '#fafaf7',
                  border: '1px solid #e5e5df',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                }}
              >
                {items.map((it) => (
                  <div key={it.id} className='d-flex justify-content-between mb-1' style={{ fontSize: 14 }}>
                    <span>
                      {itemName(it)} <span style={{ color: '#888' }}>× {it.qty}</span>
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>{(parseFloat(it.price) * it.qty).toFixed(2)} {isAr ? 'ر.س' : 'SAR'}</span>
                  </div>
                ))}
                {items.length > 0 && <hr style={{ margin: '10px 0' }} />}
                <div className='d-flex justify-content-between' style={{ fontWeight: 700, fontSize: 16 }}>
                  <span>{isAr ? 'المبلغ الإجمالي' : 'Total'}</span>
                  <span>{parseFloat(order.totalPrice).toFixed(2)} {isAr ? 'ر.س' : 'SAR'}</span>
                </div>
              </div>
            )}

            {isLoading && (
              <div className='text-center mt-5'>
                <Spinner animation='border' />
              </div>
            )}
            {(error || formError) && (
              <p className='text-center text-danger mt-4'>
                {isAr
                  ? 'تعذر تحميل صفحة الدفع، حاول مرة أخرى أو تواصل معنا.'
                  : 'Could not load the payment form. Please try again or contact us.'}
              </p>
            )}

            <div className='mysr-form'></div>

            <div className='text-center' style={{ fontSize: 13, color: '#666', marginTop: 16 }}>
              🔒 {isAr ? 'دفع آمن ومشفّر' : 'Secure encrypted payment'}
            </div>
            <div className='text-center' style={{ marginTop: 10 }}>
              <a
                href='https://wa.me/966548036906'
                target='_blank'
                rel='noreferrer'
                style={{ color: '#25D366', fontWeight: 600, fontSize: 13.5, textDecoration: 'none' }}
              >
                💬 {isAr ? 'تحتاج مساعدة؟ تواصل معنا عن طريق الواتساب' : 'Need help? Contact us on WhatsApp'}
              </a>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default MoyasarScreen;
